import { z } from 'zod';

/**
 * Assistance Request workflow contracts (Phase AR — Task #62) + the real
 * forwarding delivery channel (Task #79).
 *
 * Per Ontario IMS / Provincial Emergency Response Plan §5, the PEOC is the single
 * point of contact for municipalities and First Nations requesting provincial
 * assistance. An operator submits a request on a requestor's behalf; it routes
 * through a triage → route → assign → in_progress → fulfilled → closed lifecycle
 * (IMS Logistics resource tracking) and can be forwarded to a PEOC duty officer
 * via the real webhook delivery channel (shared with the SITREP forward retrofit).
 */

/* ------------------------------------------------------------------ *
 * Enums
 * ------------------------------------------------------------------ */

export const RequestorTypeSchema = z.enum(['municipality', 'first_nation', 'agency', 'other']);
export type RequestorType = z.infer<typeof RequestorTypeSchema>;

export const AssistanceUrgencySchema = z.enum(['routine', 'urgent', 'immediate']);
export type AssistanceUrgency = z.infer<typeof AssistanceUrgencySchema>;

export const IncidentLinkTypeSchema = z.enum([
  'high_risk_hotspot',
  'active_fire',
  'cap_alert',
  'utility_outage',
  'other',
]);
export type IncidentLinkType = z.infer<typeof IncidentLinkTypeSchema>;

export const AssistanceStatusSchema = z.enum([
  'submitted',
  'triaged',
  'routed',
  'assigned',
  'in_progress',
  'fulfilled',
  'closed',
  'cancelled',
]);
export type AssistanceStatus = z.infer<typeof AssistanceStatusSchema>;

/** Resource categories an operator can request (multi-select checkboxes in the UI). */
export const ResourceCategorySchema = z.enum([
  'shelter',
  'water',
  'medical',
  'transport',
  'aircraft',
  'crew',
  'other',
]);
export type ResourceCategory = z.infer<typeof ResourceCategorySchema>;

/* ------------------------------------------------------------------ *
 * Shared shapes
 * ------------------------------------------------------------------ */

/** Flexible requestor contact card. All fields optional. */
export const RequestorContactSchema = z.object({
  name: z.string().max(200).optional(),
  email: z.string().email().max(320).optional(),
  phone: z.string().max(50).optional(),
  role: z.string().max(200).optional(),
});
export type RequestorContact = z.infer<typeof RequestorContactSchema>;

/** Structured resource ask: categories + free-text detail + optional quantities. */
export const RequestedResourcesSchema = z.object({
  categories: z.array(ResourceCategorySchema).min(1),
  details: z.string().max(4000).optional(),
  quantities: z.record(z.union([z.number(), z.string()])).optional(),
});
export type RequestedResources = z.infer<typeof RequestedResourcesSchema>;

/** A simple lat/lon point (the form submits a point; polygons are Phase 2). */
export const GeoPointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
});
export type GeoPoint = z.infer<typeof GeoPointSchema>;

/* ------------------------------------------------------------------ *
 * Request — POST /api/assistance-requests
 * ------------------------------------------------------------------ */

export const AssistanceRequestCreateSchema = z
  .object({
    requestor_type: RequestorTypeSchema,
    requestor_name: z.string().min(1).max(300),
    requestor_contact: RequestorContactSchema.optional(),
    incident_link_type: IncidentLinkTypeSchema.optional(),
    incident_link_id: z.string().uuid().optional(),
    urgency: AssistanceUrgencySchema,
    geographic_scope: GeoPointSchema.optional(),
    requested_resources: RequestedResourcesSchema,
    request_narrative: z.string().min(1).max(8000),
    session_id: z.string().min(1).max(128),
  })
  .superRefine((val, ctx) => {
    // A link id only makes sense paired with a link type, and vice-versa.
    if ((val.incident_link_type === undefined) !== (val.incident_link_id === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'incident_link_type and incident_link_id must be provided together',
        path: ['incident_link_id'],
      });
    }
  });
export type AssistanceRequestCreate = z.infer<typeof AssistanceRequestCreateSchema>;

/* ------------------------------------------------------------------ *
 * Status transition — PATCH /api/assistance-requests/:id/status
 * ------------------------------------------------------------------ */

export const AssistanceStatusUpdateSchema = z.object({
  new_status: AssistanceStatusSchema,
  triaged_by: z.string().max(300).optional(),
  routed_to: z.string().max(300).optional(),
  assigned_to: z.string().max(300).optional(),
  fulfillment_notes: z.string().max(4000).optional(),
  /** Phase 3a (Ruling 3): triage SETS the IMS Response Level. Optional +
   *  backward-compatible — an old body that omits it still parses; persisted only
   *  on the `triaged` transition. */
  response_level: z.number().int().min(1).max(4).optional(),
  /** Phase 3a (Ruling 3): route BINDS a real delivery destination (FK into
   *  delivery_destinations). Optional + backward-compatible; persisted only on the
   *  `routed` transition, where it also mirrors the destination name into routed_to. */
  routed_destination_id: z.string().uuid().optional(),
  session_id: z.string().min(1).max(128),
});
export type AssistanceStatusUpdate = z.infer<typeof AssistanceStatusUpdateSchema>;

/* ------------------------------------------------------------------ *
 * Forward — POST /api/assistance-requests/:id/forward
 * ------------------------------------------------------------------ */

export const AssistanceForwardRequestSchema = z.object({
  session_id: z.string().min(1).max(128),
  /** Registry destination to route to (migration 0052). Omitted → default PEOC. */
  destination_id: z.string().uuid().optional(),
});
export type AssistanceForwardRequest = z.infer<typeof AssistanceForwardRequestSchema>;

/** The delivery result returned by a forward action (assistance request OR SITREP). */
export const DeliveryResultSchema = z.object({
  delivered: z.boolean(),
  delivery_log_id: z.string(),
  delivery_channel: z.enum(['webhook', 'email', 'console_log']),
  response_status: z.number().int().nullable(),
  error: z.string().nullable(),
  /** The destination this forward was routed to (migration 0052). Null when none
   *  resolved (env/console fallback). Present on every forward path's result. */
  destination: z
    .object({
      id: z.string().nullable(),
      name: z.string().nullable(),
      channel: z.enum(['webhook', 'email']),
      address: z.string().nullable(),
    })
    .nullable()
    .optional(),
});
export type DeliveryResult = z.infer<typeof DeliveryResultSchema>;

/* ------------------------------------------------------------------ *
 * Response — full row
 * ------------------------------------------------------------------ */

/** #425 LINK — a light summary of an agency_task issued in service of this request,
 *  surfaced read-only so the originator sees the down-task status without a second
 *  fetch (the "your request is now being worked" read-back). */
export const AssistanceRequestDownTaskSchema = z.object({
  id: z.string(),
  task_number: z.string(),
  target_agency_name: z.string().nullable(),
  task_status: z.string(),
  urgency: z.string(),
  created_at: z.string(),
});
export type AssistanceRequestDownTask = z.infer<typeof AssistanceRequestDownTaskSchema>;

export const AssistanceRequestSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  operator_id: z.string(),
  session_id: z.string(),

  requestor_type: RequestorTypeSchema,
  requestor_name: z.string(),
  requestor_contact: RequestorContactSchema.nullable(),

  incident_link_type: IncidentLinkTypeSchema.nullable(),
  incident_link_id: z.string().nullable(),
  incident_summary: z.string().nullable(),

  urgency: AssistanceUrgencySchema,
  geographic_scope: GeoPointSchema.nullable(),
  requested_resources: RequestedResourcesSchema,
  request_narrative: z.string(),

  status: AssistanceStatusSchema,
  /** #419 closed-loop — the PEOC "received" receipt (migration 0113). Orthogonal to
   *  status (mirrors forwarded_at; NO 8th status). Set explicitly via the ack action
   *  OR auto-back-filled on triage (coalesce). acknowledged_by names the acknowledging
   *  PEOC operator (null on the triage auto-stamp path). */
  acknowledged_at: z.string().nullable(),
  acknowledged_by: z.string().nullable(),
  triaged_at: z.string().nullable(),
  triaged_by: z.string().nullable(),
  routed_at: z.string().nullable(),
  routed_to: z.string().nullable(),
  /** The routed-destination binding (Ruling 3 / Phase 3a, migration 0099). */
  routed_destination_id: z.string().nullable(),
  assigned_at: z.string().nullable(),
  assigned_to: z.string().nullable(),
  fulfilled_at: z.string().nullable(),
  fulfillment_notes: z.string().nullable(),
  closed_at: z.string().nullable(),

  forwarded_at: z.string().nullable(),
  forwarded_to: z.string().nullable(),
  forwarded_payload_id: z.string().nullable(),

  response_level: z.number().int().min(1).max(4).nullable(),

  /** The resource_request raised on assign (Ruling 3 / Phase 3b-1, migration 0100):
   *  the most-recent resource_request linked back to this request, surfaced so the
   *  originator SEES the bound resource (id / RR-YYYY-NNNNN / lifecycle status) on the
   *  request — not a free-text name (§H.5). Null until assigned; optional for
   *  backward-compat with older payloads / fixtures that omit it. */
  assigned_resource_request: z
    .object({
      id: z.string(),
      request_number: z.string(),
      request_status: z.string(),
    })
    .nullable()
    .optional(),

  /** #425 LINK — the tracked incident this request was attached to, surfaced as a
   *  requestor-side read-back ("your request is now INC-YYYY-NNNNN"). Null until linked;
   *  optional for backward-compat with older payloads / fixtures. */
  incident_id: z.string().nullable().optional(),
  incident_number: z.string().nullable().optional(),
  /** #425 LINK — agency_tasks issued in service of this request, with live status. */
  down_tasks: z.array(AssistanceRequestDownTaskSchema).optional(),
});
export type AssistanceRequest = z.infer<typeof AssistanceRequestSchema>;

/** Compact list-item for the triage queue (omits the long narrative/snapshot). */
export const AssistanceRequestListItemSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  requestor_type: RequestorTypeSchema,
  requestor_name: z.string(),
  urgency: AssistanceUrgencySchema,
  status: AssistanceStatusSchema,
  response_level: z.number().int().min(1).max(4).nullable(),
  incident_link_type: IncidentLinkTypeSchema.nullable(),
  categories: z.array(z.string()),
  forwarded_at: z.string().nullable(),
  /** The request's map-locatable point (#426), so a worklist row can fly the COP to
   *  it without opening the detail. Null when the request carries no location.
   *  Optional for backward-compat with older payloads / fixtures that omit it. */
  geographic_scope: GeoPointSchema.nullable().optional(),
  /** #419 closed-loop — the PEOC receipt stamp, carried on the LIST so the requestor's
   *  "My Requests" tray shows "Received by PEOC · HH:MM" (a DISCRETE badge, off the
   *  status stepper) without opening the detail. Optional for the lean triage-queue
   *  callers that ignore it. */
  acknowledged_at: z.string().nullable().optional(),
  acknowledged_by: z.string().nullable().optional(),
  /** #419 — the requestor read-backs ALSO carried on the list (load-bearing for the
   *  "My Requests" tray: it hits this LIST endpoint, so the incident #, the down-tasks,
   *  and the bound resource it tracks must travel here, not only on loadRequest). Mirror
   *  the full-schema fields; optional for backward-compat with the lean callers. */
  incident_id: z.string().nullable().optional(),
  incident_number: z.string().nullable().optional(),
  assigned_resource_request: z
    .object({
      id: z.string(),
      request_number: z.string(),
      request_status: z.string(),
    })
    .nullable()
    .optional(),
  down_tasks: z.array(AssistanceRequestDownTaskSchema).optional(),
});
export type AssistanceRequestListItem = z.infer<typeof AssistanceRequestListItemSchema>;

export const AssistanceRequestListResponseSchema = z.object({
  requests: z.array(AssistanceRequestListItemSchema),
});
export type AssistanceRequestListResponse = z.infer<typeof AssistanceRequestListResponseSchema>;
